import React, { useState, useEffect } from "react";
import { AdminSideMenu } from "../admin/AdminMenu";
import API_URL from "../../api/API";
import { useAdminAuthContext } from "../admin/useAdminAuthContext";
import { Link } from "react-router-dom";


export const AdminManageBusinessType = () => {
  const { admin } = useAdminAuthContext();

  // Business Types & Pagination
  const [businessTypes, setBusinessTypes] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Create Business Type Modal
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Services
  const [expandedType, setExpandedType] = useState(null);
  const [services, setServices] = useState({});
  const [newServiceName, setNewServiceName] = useState("");
  const [newServiceDesc, setNewServiceDesc] = useState("");
  const [isAddingService, setIsAddingService] = useState(false);

  // Editing states
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [editingTypeName, setEditingTypeName] = useState("");
  const [editingTypeDesc, setEditingTypeDesc] = useState("");

  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editingServiceName, setEditingServiceName] = useState("");
  const [editingServiceDesc, setEditingServiceDesc] = useState("");

  // Fetch business types
  const fetchBusinessTypes = async (page = 1) => {
    try {
      setIsLoading(true);
      setError("");
      const response = await fetch(
        `${API_URL.ADMIN_MANAGE_BUSINESS_TYPE}?page=${page}&limit=50`,
        { headers: { 
          Authorization: `Bearer ${admin.token}`,
          "x-session-id": admin.sessionId,             
          "x-frontend-url": window.location.pathname, 
        } }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to fetch business types");

      setBusinessTypes(data.businessTypes || []);
      setFilteredData(data.businessTypes || []);
      setCurrentPage(data.page || 1);
      setTotalPages(data.totalPages || 1);
      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  // Fetch services for a business type
  const fetchServices = async (businessTypeId) => {
    try {
      const response = await fetch(`${API_URL.ADMIN_GET_SERVICES}${businessTypeId}`, {
        headers: { Authorization: `Bearer ${admin.token}`,
          "x-session-id": admin.sessionId,             
          "x-frontend-url": window.location.pathname, },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to fetch services");
      setServices((prev) => ({ ...prev, [businessTypeId]: data.services || [] }));
    } catch (err) {
      alert(err.message);
    }
  };

  // Create business type
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setError("Name is required");
    try {
      setIsSubmitting(true);
      const response = await fetch(API_URL.ADMIN_CREATE_BUSINESS_TYPE, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${admin.token}`,
          "x-session-id": admin.sessionId,             
          "x-frontend-url": window.location.pathname, },
        body: JSON.stringify({ name, description }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to create business type");

      alert("Business type created!");
      setShowModal(false);
      setName("");
      setDescription("");
      fetchBusinessTypes(currentPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete business type
  const deleteBusinessType = async (id) => {
    try {
      setIsDeleting(true);
      const response = await fetch(`${API_URL.ADMIN_DELETE_BUSINESS_TYPE}${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${admin.token}`,
          "x-session-id": admin.sessionId,             
          "x-frontend-url": window.location.pathname, },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to delete business type");

      alert("Business type deleted successfully!");
      fetchBusinessTypes(currentPage);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this business type?")) {
      deleteBusinessType(id);
    }
  };

  // Add service
  const handleAddService = async (businessTypeId) => {
    if (!newServiceName.trim()) {
      alert("Service name is required");
      return;
    }
    try {
      setIsAddingService(true);
      const response = await fetch(API_URL.ADMIN_CREATE_SERVICE, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${admin.token}`,
          "x-session-id": admin.sessionId,             
          "x-frontend-url": window.location.pathname, },
        body: JSON.stringify({ businessTypeId, name: newServiceName, description: newServiceDesc }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to create service");

      alert("Service added!");
      setNewServiceName("");
      setNewServiceDesc("");
      fetchServices(businessTypeId);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsAddingService(false);
    }
  };

  // Delete service
  const handleDeleteService = async (id, businessTypeId) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      const response = await fetch(`${API_URL.ADMIN_DELETE_SERVICE}${id}?businessTypeId=${businessTypeId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${admin.token}`,
          "x-session-id": admin.sessionId,             
          "x-frontend-url": window.location.pathname, },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to delete service");

      alert("Service deleted!");
      fetchServices(businessTypeId);
    } catch (err) {
      alert(err.message);
    }
  };

  // Start editing business type
  const startEditType = (bt) => {
    setEditingTypeId(bt._id);
    setEditingTypeName(bt.name);
    setEditingTypeDesc(bt.description || "");
  };

  const saveEditType = async (id) => {
    try {
      const response = await fetch(`${API_URL.ADMIN_UPDATE_BUSINESS_TYPE}${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${admin.token}`,
          "x-session-id": admin.sessionId,             
          "x-frontend-url": window.location.pathname, },
        body: JSON.stringify({ name: editingTypeName, description: editingTypeDesc }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to update business type");

      alert("Business type updated!");
      setEditingTypeId(null);
      fetchBusinessTypes(currentPage);
    } catch (err) {
      alert(err.message);
    }
  };

  // Start editing service
  const startEditService = (svc) => {
    setEditingServiceId(svc._id);
    setEditingServiceName(svc.name);
    setEditingServiceDesc(svc.description || "");
  };

  const saveEditService = async (serviceId, businessTypeId) => {
    try {
      const response = await fetch(`${API_URL.ADMIN_UPDATE_SERVICE}${serviceId}?businessTypeId=${businessTypeId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${admin.token}`,
          "x-session-id": admin.sessionId,             
          "x-frontend-url": window.location.pathname, },
        body: JSON.stringify({ name: editingServiceName, description: editingServiceDesc }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to update service");

      alert("Service updated!");
      setEditingServiceId(null);
      fetchServices(businessTypeId);
    } catch (err) {
      alert(err.message);
    }
  };

  // Search
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) return setFilteredData(businessTypes);
    const lower = query.toLowerCase();
    setFilteredData(
      businessTypes.filter(
        (bt) => bt.name.toLowerCase().includes(lower) || (bt.description && bt.description.toLowerCase().includes(lower))
      )
    );
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  useEffect(() => {
    fetchBusinessTypes(currentPage);
  }, [currentPage]);

  return (
    <div className="flex h-screen">
      <AdminSideMenu />

      <div className="flex-1 p-6 bg-gray-100 pt-16 flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Manage Business Types</h1>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-700 text-white rounded hover:bg-blue-800"
          >
            Create Business Type
          </button>
        </div>

        {/* Search */}
        <div className="flex justify-end mb-4">
          <input
            type="text"
            placeholder="Search business types..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="p-2 border rounded w-1/3"
          />
        </div>

        {/* List */}
        <div className="flex-1 overflow-auto space-y-4">
          {isLoading ? (
            <p className="text-center text-blue-600 py-4">Loading...</p>
          ) : error ? (
            <p className="text-center text-red-600 py-4">{error}</p>
          ) : filteredData.length === 0 ? (
            <p className="text-center py-4 text-gray-600">No records found</p>
          ) : (
            filteredData.map((bt) => (
              <div key={bt._id} className="border rounded shadow bg-white">
                {/* Business Type Header */}
                <div className="flex justify-between items-center p-4 bg-blue-100 rounded-t">
                  <div className="flex flex-col">
                    {editingTypeId === bt._id ? (
                      <>
                      <div className="flex flex-col space-y-2 w-80">
                            <input
                                type="text"
                                value={editingTypeName}
                                onChange={(e) => setEditingTypeName(e.target.value)}
                                className="p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                placeholder="Business Type Name"
                            />
                            <textarea
                                value={editingTypeDesc}
                                onChange={(e) => setEditingTypeDesc(e.target.value)}
                                className="p-2 border rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                placeholder="Business Type Description"
                                rows={3}
                            />
                            </div>
                      </>
                    ) : (
                      <>
                        <h2 className="font-semibold text-lg">{bt.name}</h2>
                        <p className="text-gray-700">{bt.description}</p>
                      </>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {editingTypeId === bt._id ? (
                      <>
                        <button
                          onClick={() => saveEditType(bt._id)}
                          className="px-2 py-1 bg-green-500 text-white rounded"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingTypeId(null)}
                          className="px-2 py-1 bg-gray-400 text-white rounded"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEditType(bt)}
                          className="px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            expandedType === bt._id
                              ? setExpandedType(null)
                              : (setExpandedType(bt._id), fetchServices(bt._id))
                          }
                          className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                          {expandedType === bt._id ? "Hide Services" : "View Services"}
                        </button>
                        <button
                          onClick={() => handleDelete(bt._id)}
                          className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                          disabled={isDeleting}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Services */}
                {expandedType === bt._id && (
                  <div className="p-4 bg-gray-50 space-y-4">
                    {/* Add Service */}
                    <div className="flex gap-2 mb-4">
                      <input
                        type="text"
                        placeholder="Service name"
                        value={newServiceName}
                        onChange={(e) => setNewServiceName(e.target.value)}
                        className="p-2 border rounded w-1/3"
                      />
                      <input
                        type="text"
                        placeholder="Description"
                        value={newServiceDesc}
                        onChange={(e) => setNewServiceDesc(e.target.value)}
                        className="p-2 border rounded w-1/2"
                      />
                      <button
                        onClick={() => handleAddService(bt._id)}
                        disabled={isAddingService}
                        className="px-3 py-2 bg-green-600 text-white rounded"
                      >
                        {isAddingService ? "Adding..." : "Add Service"}
                      </button>
                    </div>

                    {/* Services Table */}
                    <table className="w-full border">
                      <thead className="bg-green-100">
                        <tr>
                          <th className="px-4 py-2 text-left">Service Name</th>
                          <th className="px-4 py-2 text-left">Description</th>
                          <th className="px-4 py-2 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {services[bt._id]?.length > 0 ? (
                          services[bt._id].map((svc) => (
                            <tr key={svc._id} className="border-b hover:bg-green-50">
                              <td className="px-4 py-2">
                                {editingServiceId === svc._id ? (
                                  <input
                                    type="text"
                                    value={editingServiceName}
                                    onChange={(e) => setEditingServiceName(e.target.value)}
                                    className="p-1 border rounded w-full"
                                  />
                                ) : (
                                  svc.name
                                )}
                              </td>
                              <td className="px-4 py-2">
                                {editingServiceId === svc._id ? (
                                  <input
                                    type="text"
                                    value={editingServiceDesc}
                                    onChange={(e) => setEditingServiceDesc(e.target.value)}
                                    className="p-1 border rounded w-full"
                                  />
                                ) : (
                                  svc.description || "-"
                                )}
                              </td>
                              <td className="px-4 py-2 flex gap-1">
                                {editingServiceId === svc._id ? (
                                  <>
                                    <button
                                      onClick={() => saveEditService(svc._id, bt._id)}
                                      className="px-2 py-1 bg-green-500 text-white rounded"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingServiceId(null)}
                                      className="px-2 py-1 bg-gray-400 text-white rounded"
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => startEditService(svc)}
                                      className="px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteService(svc._id, bt._id)}
                                      className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                                    >
                                      Delete
                                    </button>
                                    <Link
                                      to={`/administrator/waiverbuilder/configure/${svc._id}`}
                                      className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                                    >
                                      Configure Waiver
                                    </Link>
                                  </>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center py-2 text-gray-500">
                              No services found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded ${
              currentPage === 1 ? "bg-gray-300 cursor-not-allowed" : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Previous
          </button>
          <p className="text-sm">
            Page {currentPage} of {totalPages}
          </p>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded ${
              currentPage === totalPages ? "bg-gray-300 cursor-not-allowed" : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Create Business Type</h2>
            {error && <p className="text-red-600 mb-2">{error}</p>}
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="block font-semibold mb-1">Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block font-semibold mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full p-2 border rounded"
                  rows="3"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 rounded text-white ${
                    isSubmitting ? "bg-blue-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {isSubmitting ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
